/**
 * Invoice Allocation Modal
 * 
 * Allows allocating a Xero invoice across multiple shipments.
 * Supports:
 * - Auto-detecting shipments from contract reference
 * - Even dollar split as default
 * - Edit amount -> auto-calculate % of shipment value
 * - Edit % -> auto-calculate amount
 * - Adding "Other" allocations (fees, commission, etc.)
 * - Saving with unallocated remainder
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Table,
  InputNumber,
  Button,
  Space,
  Typography,
  Alert,
  Spin,
  Input,
  Divider,
  Card,
  Descriptions,
  message,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import api, { xeroApi } from '../../services/api';

const { Text, Title } = Typography;

// Color constants matching design system
const colors = {
  success: '#169959',
  error: '#C32928',
  warning: '#E6B339',
  info: '#179DB5',
  textSecondary: '#8C8C8C',
};

interface Invoice {
  invoiceId: string;
  invoiceNumber: string;
  contact: string;
  date: string;
  dueDate: string;
  total: number;
  amountDue: number;
  reference?: string;
  suggestedMatches?: Array<{
    shipmentId: string;
    reference: string;
    contractReference: string;
    confidence: number;
  }>;
}

interface ShipmentForAllocation {
  id: string;
  reference: string;
  shipmentNumber: number;
  status: string;
  estimatedValue: number | null;
}

interface AllocationRow {
  key: string;
  shipmentId?: string;
  shipmentRef?: string;
  description?: string;
  shipmentValue: number | null;
  amount: number;
  percentage: number;
  isOther: boolean;
}

interface InvoiceAllocationModalProps {
  open: boolean;
  invoice: Invoice | null;
  onClose: () => void;
  onSuccess: () => void;
}

const InvoiceAllocationModal: React.FC<InvoiceAllocationModalProps> = ({
  open,
  invoice,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [allocations, setAllocations] = useState<AllocationRow[]>([]);
  const [contractRef, setContractRef] = useState<string>('');

  // Extract contract ref from invoice reference or suggested matches
  useEffect(() => {
    if (invoice) {
      // Try to extract from reference (e.g., "CR-SP25011, 54-0102-0103-0104")
      const refMatch = invoice.reference?.match(/CR-[A-Z]{2}\d{4,5}/i);
      if (refMatch) {
        setContractRef(refMatch[0]);
      } else if (invoice.suggestedMatches?.[0]?.contractReference) {
        setContractRef(invoice.suggestedMatches[0].contractReference);
      } else {
        setContractRef('');
      }
    }
  }, [invoice]);

  // Fetch shipments for the contract
  const { data: contractData, isLoading: loadingShipments } = useQuery({
    queryKey: ['contract-shipments', contractRef],
    queryFn: async () => {
      if (!contractRef) return null;
      const response = await api.get(`/xero/contracts/${contractRef}/shipments`);
      return response as unknown as { contractRef: string; shipments: ShipmentForAllocation[] };
    },
    enabled: !!contractRef && open,
  });

  // Initialize allocations when shipments are loaded
  useEffect(() => {
    if (contractData?.shipments && invoice && allocations.length === 0) {
      const shipments = contractData.shipments;
      const evenSplit = invoice.total / shipments.length;

      const initialAllocations: AllocationRow[] = shipments.map((s, idx) => ({
        key: `shipment-${idx}`,
        shipmentId: s.id,
        shipmentRef: s.reference,
        shipmentValue: s.estimatedValue,
        amount: Math.round(evenSplit * 100) / 100, // Round to 2 decimal places
        percentage: s.estimatedValue ? (evenSplit / s.estimatedValue) * 100 : 0,
        isOther: false,
      }));

      // Adjust last allocation to account for rounding
      if (initialAllocations.length > 0) {
        const totalAllocated = initialAllocations.reduce((sum, a) => sum + a.amount, 0);
        const diff = invoice.total - totalAllocated;
        const lastAlloc = initialAllocations[initialAllocations.length - 1];
        if (lastAlloc) {
          lastAlloc.amount += diff;
          
          // Recalculate percentage for last item
          if (lastAlloc.shipmentValue) {
            lastAlloc.percentage = (lastAlloc.amount / lastAlloc.shipmentValue) * 100;
          }
        }
      }

      setAllocations(initialAllocations);
    }
  }, [contractData, invoice, allocations.length]);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setAllocations([]);
      setContractRef('');
    }
  }, [open]);

  // Save allocations mutation
  const saveAllocationsMutation = useMutation({
    mutationFn: async (data: { allocations: Array<{ shipmentId?: string; description?: string; amount: number; percentage: number }> }) => {
      if (!invoice) throw new Error('No invoice selected');
      return api.post(`/xero/invoices/${invoice.invoiceId}/allocations`, data);
    },
    onSuccess: () => {
      message.success('Allocations saved successfully');
      queryClient.invalidateQueries({ queryKey: ['unmatched-invoices'] });
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to save allocations');
    },
  });

  // Calculate totals
  const totalAllocated = allocations.reduce((sum, a) => sum + a.amount, 0);
  const remaining = (invoice?.total || 0) - totalAllocated;
  const isFullyAllocated = Math.abs(remaining) < 0.01;

  // Handle amount change
  const handleAmountChange = useCallback((key: string, value: number | null) => {
    setAllocations(prev => prev.map(a => {
      if (a.key !== key) return a;
      const amount = value || 0;
      const percentage = a.shipmentValue ? (amount / a.shipmentValue) * 100 : 0;
      return { ...a, amount, percentage };
    }));
  }, []);

  // Handle percentage change
  const handlePercentageChange = useCallback((key: string, value: number | null) => {
    setAllocations(prev => prev.map(a => {
      if (a.key !== key) return a;
      const percentage = value || 0;
      const amount = a.shipmentValue ? (a.shipmentValue * percentage) / 100 : 0;
      return { ...a, amount: Math.round(amount * 100) / 100, percentage };
    }));
  }, []);

  // Handle description change for "other" rows
  const handleDescriptionChange = useCallback((key: string, value: string) => {
    setAllocations(prev => prev.map(a => {
      if (a.key !== key) return a;
      return { ...a, description: value };
    }));
  }, []);

  // Add "other" allocation row
  const addOtherAllocation = useCallback(() => {
    setAllocations(prev => [
      ...prev,
      {
        key: `other-${Date.now()}`,
        description: '',
        shipmentValue: null,
        amount: remaining > 0 ? remaining : 0,
        percentage: 0,
        isOther: true,
      },
    ]);
  }, [remaining]);

  // Remove allocation row
  const removeAllocation = useCallback((key: string) => {
    setAllocations(prev => prev.filter(a => a.key !== key));
  }, []);

  // Handle save
  const handleSave = useCallback(() => {
    // Validate allocations
    for (const alloc of allocations) {
      if (alloc.isOther && !alloc.description?.trim()) {
        message.error('Please provide a description for all "Other" allocations');
        return;
      }
      if (alloc.amount <= 0) {
        message.error('All allocation amounts must be greater than 0');
        return;
      }
    }

    saveAllocationsMutation.mutate({
      allocations: allocations.map(a => ({
        shipmentId: a.shipmentId,
        description: a.description,
        amount: a.amount,
        percentage: a.percentage,
      })),
    });
  }, [allocations, saveAllocationsMutation]);

  // Table columns
  const columns = [
    {
      title: 'Shipment / Description',
      key: 'shipment',
      render: (_: any, record: AllocationRow) => {
        if (record.isOther) {
          return (
            <Input
              placeholder="e.g., Bank fees, Commission"
              value={record.description}
              onChange={(e) => handleDescriptionChange(record.key, e.target.value)}
              style={{ width: '100%' }}
            />
          );
        }
        return (
          <Space direction="vertical" size={0}>
            <Text strong>{record.shipmentRef}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.shipmentValue 
                ? `Value: $${record.shipmentValue.toLocaleString()}` 
                : 'Value: Unknown'}
            </Text>
          </Space>
        );
      },
    },
    {
      title: 'Shipment Value',
      key: 'shipmentValue',
      width: 140,
      align: 'right' as const,
      render: (_: any, record: AllocationRow) => {
        if (record.isOther || !record.shipmentValue) {
          return <Text type="secondary">-</Text>;
        }
        return <Text>${record.shipmentValue.toLocaleString()}</Text>;
      },
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 140,
      render: (_: any, record: AllocationRow) => (
        <InputNumber
          value={record.amount}
          onChange={(v) => handleAmountChange(record.key, v)}
          prefix="$"
          precision={2}
          min={0}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '%',
      key: 'percentage',
      width: 100,
      render: (_: any, record: AllocationRow) => {
        if (record.isOther || !record.shipmentValue) {
          return <Text type="secondary">-</Text>;
        }
        return (
          <InputNumber
            value={Math.round(record.percentage * 100) / 100}
            onChange={(v) => handlePercentageChange(record.key, v)}
            suffix="%"
            precision={2}
            min={0}
            max={100}
            style={{ width: '100%' }}
          />
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: any, record: AllocationRow) => {
        if (record.isOther) {
          return (
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => removeAllocation(record.key)}
            />
          );
        }
        return null;
      },
    },
  ];

  if (!invoice) return null;

  return (
    <Modal
      title="Allocate Invoice to Shipments"
      open={open}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saveAllocationsMutation.isPending}
          onClick={handleSave}
          disabled={allocations.length === 0}
        >
          Save Allocations
        </Button>,
      ]}
    >
      {/* Invoice Details */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Invoice">{invoice.invoiceNumber}</Descriptions.Item>
          <Descriptions.Item label="Contact">{invoice.contact}</Descriptions.Item>
          <Descriptions.Item label="Total">
            <Text strong style={{ fontSize: 16 }}>${invoice.total.toFixed(2)}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Date">{dayjs(invoice.date).format('MMM D, YYYY')}</Descriptions.Item>
          <Descriptions.Item label="Reference" span={2}>{invoice.reference || '-'}</Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Contract Search */}
      <div style={{ marginBottom: 16 }}>
        <Text strong>Contract Reference:</Text>
        <Input
          value={contractRef}
          onChange={(e) => {
            setContractRef(e.target.value.toUpperCase());
            setAllocations([]); // Reset allocations when contract changes
          }}
          placeholder="e.g., CR-SP25011"
          style={{ width: 200, marginLeft: 8 }}
        />
        {loadingShipments && <Spin size="small" style={{ marginLeft: 8 }} />}
      </div>

      {/* No shipments warning */}
      {contractRef && !loadingShipments && !contractData?.shipments?.length && (
        <Alert
          message="No shipments found"
          description={`No shipments found for contract ${contractRef}. Check the contract reference.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Allocations Table */}
      {allocations.length > 0 && (
        <>
          <Table
            dataSource={allocations}
            columns={columns}
            pagination={false}
            size="small"
            rowKey="key"
          />

          <Divider style={{ margin: '12px 0' }} />

          {/* Add Other button */}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={addOtherAllocation}
            style={{ marginBottom: 16 }}
          >
            Add Other (fees, commission, etc.)
          </Button>

          {/* Totals */}
          <Card size="small" style={{ backgroundColor: isFullyAllocated ? '#f6ffed' : '#fffbe6' }}>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <div>
                <Text strong>Allocated: </Text>
                <Text>${totalAllocated.toFixed(2)}</Text>
              </div>
              <div>
                <Text strong>Remaining: </Text>
                <Text style={{ color: isFullyAllocated ? colors.success : colors.warning }}>
                  ${remaining.toFixed(2)}
                </Text>
                {isFullyAllocated ? (
                  <CheckCircleOutlined style={{ marginLeft: 8, color: colors.success }} />
                ) : (
                  <WarningOutlined style={{ marginLeft: 8, color: colors.warning }} />
                )}
              </div>
            </Space>
          </Card>

          {/* Remaining warning */}
          {!isFullyAllocated && remaining > 0 && (
            <Alert
              message={`$${remaining.toFixed(2)} unallocated`}
              description="You can save with unallocated amount, or add more allocations."
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </>
      )}

      {/* Initial state - no contract selected */}
      {!contractRef && (
        <Alert
          message="Enter a contract reference"
          description="Enter the contract reference to load shipments for allocation."
          type="info"
          showIcon
        />
      )}
    </Modal>
  );
};

export default InvoiceAllocationModal;
